/**
 * <racket-quiz>
 *
 * Intro -> 4 questions -> results.
 *
 * Products come from Liquid inside an inert <template data-quiz-products>, in batches of 50
 * (one batch per "page"). The first batch is in the page itself. When the customer clicks
 * "Start", the remaining batches are fetched from this same section through the Section
 * Rendering API (?section_id=...&page=N), so the quiz can search a collection of any size
 * without making the first page load heavy.
 *
 * Matching rules
 *   level  : product has the tag chosen (Beginner / Intermediate / Advanced)
 *   style  : product has the tag chosen (Power / Control / Power/Control)
 *   shape  : product has the tag chosen (Round / Teardrop / Diamond)
 *            (the "type = Racket" requirement is applied in Liquid, so every product here is a racket)
 *   budget : price falls in the chosen range (break points come from the section settings)
 *
 * If at least one racket matches all four answers, every perfect match is shown.
 * Otherwise the closest rackets are shown, ranked by WEIGHTS below, each with a note
 * saying which answers it differs on.
 */
if (!customElements.get('racket-quiz')) {
  // How much each answer counts when no racket matches everything. Adjust to taste.
  const WEIGHTS = { level: 4, budget: 3, style: 2, shape: 2 };

  class RacketQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.intro = this.querySelector('[data-quiz-intro]');
      this.flow = this.querySelector('[data-quiz-flow]');
      this.questions = this.querySelector('[data-quiz-questions]');
      this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
      this.backButton = this.querySelector('[data-quiz-back]');
      this.nextButton = this.querySelector('[data-quiz-next]');
      this.results = this.querySelector('[data-quiz-results]');
      this.resultsTitle = this.querySelector('[data-quiz-results-title]');
      this.summary = this.querySelector('[data-quiz-summary]');
      this.grid = this.querySelector('[data-quiz-grid]');
      this.pool = this.querySelector('template[data-quiz-products]');
      this.current = 0;
      this.busy = false;

      // Product batches: page number -> array of <li> elements
      this.currentPage = Number(this.pool && this.pool.dataset.currentPage) || 1;
      this.totalPages = Number(this.pool && this.pool.dataset.totalPages) || 1;
      this.pages = new Map();
      this.inFlight = new Map();
      this.products = null;
      this.productsBuiltFrom = 0;
      if (this.pool) {
        this.pages.set(this.currentPage, Array.from(this.pool.content.querySelectorAll('[data-quiz-product]')));
      }

      this.querySelector('[data-quiz-start]').addEventListener('click', () => this.start());
      this.backButton.addEventListener('click', () => this.goTo(this.current - 1));
      this.nextButton.addEventListener('click', () => this.next());
      this.querySelector('[data-quiz-restart]').addEventListener('click', () => this.restart());

      this.addEventListener('change', (event) => {
        if (event.target.matches('.racket-quiz__input')) this.updateNextState();
      });

      // Enter on a chosen option moves on, so keyboard users don't have to tab to "Next"
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('.racket-quiz__input')) {
          event.preventDefault();
          this.next();
        }
      });
    }

    /* ---------- Flow ---------- */

    start() {
      this.intro.hidden = true;
      this.flow.hidden = false;
      this.results.hidden = true;
      this.questions.hidden = false;
      this.goTo(0);
      this.bringIntoView(this.flow);

      // Download the other product batches while the customer answers the questions
      this.loadMissingPages();
    }

    goTo(index) {
      this.current = Math.max(0, Math.min(index, this.steps.length - 1));
      const isLast = this.current === this.steps.length - 1;

      this.steps.forEach((step, i) => {
        step.hidden = i !== this.current;
      });
      this.backButton.hidden = this.current === 0;
      this.nextButton.textContent = isLast
        ? this.nextButton.dataset.finishLabel
        : this.nextButton.dataset.nextLabel;

      this.updateNextState();
      this.steps[this.current].querySelector('[data-quiz-step-title]').focus({ preventScroll: true });
    }

    next() {
      if (this.nextButton.disabled) return;
      if (this.current < this.steps.length - 1) {
        this.goTo(this.current + 1);
      } else {
        this.showResults();
      }
    }

    updateNextState() {
      this.nextButton.disabled = !this.steps[this.current].querySelector('input:checked');
    }

    restart() {
      this.querySelectorAll('.racket-quiz__input').forEach((input) => {
        input.checked = false;
      });
      this.grid.textContent = '';
      this.results.hidden = true;
      this.questions.hidden = false;
      this.goTo(0);
      this.bringIntoView(this.flow);
    }

    /* ---------- Product loading ---------- */

    hasMissingPages() {
      for (let page = 1; page <= this.totalPages; page++) {
        if (!this.pages.has(page)) return true;
      }
      return false;
    }

    // Fetches every batch we don't have yet. A batch that fails is simply retried next time.
    loadMissingPages() {
      const requests = [];
      for (let page = 1; page <= this.totalPages; page++) {
        if (this.pages.has(page)) continue;
        if (!this.inFlight.has(page)) {
          const request = this.fetchPage(page)
            .then((items) => {
              this.pages.set(page, items);
            })
            .catch((error) => {
              console.warn(`racket-quiz: could not load product batch ${page}`, error);
            })
            .then(() => {
              this.inFlight.delete(page);
            });
          this.inFlight.set(page, request);
        }
        requests.push(this.inFlight.get(page));
      }
      return Promise.all(requests);
    }

    fetchPage(page) {
      // Keep the current query string (e.g. preview_theme_id in the theme editor) and add ours
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('page', page);

      return fetch(url.toString(), { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const template = doc.querySelector('template[data-quiz-products]');
          return template ? Array.from(template.content.querySelectorAll('[data-quiz-product]')) : [];
        });
    }

    async getProducts() {
      await this.loadMissingPages();

      if (this.products && this.productsBuiltFrom === this.pages.size) return this.products;

      const products = [];
      Array.from(this.pages.keys())
        .sort((a, b) => a - b)
        .forEach((page) => {
          this.pages.get(page).forEach((el, i) => {
            let tags = [];
            try {
              tags = JSON.parse(el.dataset.tags).map((tag) => String(tag).trim().toLowerCase());
            } catch (error) {
              tags = [];
            }
            // "order" keeps the collection's own order, batch by batch
            products.push({ el, order: page * 1000 + i, tags, price: Number(el.dataset.price) });
          });
        });

      this.products = products;
      this.productsBuiltFrom = this.pages.size;
      return products;
    }

    /* ---------- Matching ---------- */

    getAnswers() {
      return this.steps.map((step) => {
        const input = step.querySelector('input:checked');
        const label = input.closest('label').querySelector('.racket-quiz__option-label');
        return {
          key: step.dataset.criterion,
          name: step.dataset.name,
          value: input.value,
          label: label ? label.textContent.trim() : input.value,
        };
      });
    }

    inBudget(price, range) {
      const low = Number(this.dataset.budgetLow);
      const high = Number(this.dataset.budgetHigh);
      if (range === 'low') return price < low;
      if (range === 'high') return price >= high;
      return price >= low && price <= high;
    }

    matches(product, answer) {
      if (answer.key === 'budget') return this.inBudget(product.price, answer.value);
      return product.tags.includes(answer.value.trim().toLowerCase());
    }

    /* ---------- Results ---------- */

    async showResults() {
      if (this.busy) return;
      this.busy = true;

      // Normally the batches finished downloading while the customer was answering.
      // If not, say so on the button instead of leaving it looking frozen.
      const waiting = this.hasMissingPages();
      if (waiting) {
        this.nextButton.disabled = true;
        this.nextButton.setAttribute('aria-busy', 'true');
        this.nextButton.textContent = this.nextButton.dataset.loadingLabel;
      }

      let products;
      try {
        products = await this.getProducts();
      } finally {
        this.busy = false;
        if (waiting) {
          this.nextButton.removeAttribute('aria-busy');
          this.nextButton.textContent = this.nextButton.dataset.finishLabel;
          this.updateNextState();
        }
      }

      const answers = this.getAnswers();

      const scored = products.map((product) => {
        const missed = answers.filter((answer) => !this.matches(product, answer));
        const score = answers.reduce(
          (total, answer) => total + (missed.includes(answer) ? 0 : WEIGHTS[answer.key] || 1),
          0
        );
        return { ...product, missed, score };
      });

      const perfect = scored.filter((product) => product.missed.length === 0);
      let list;
      let mode;

      if (perfect.length) {
        list = perfect;
        mode = perfect.length === 1 ? 'exact-one' : 'exact-many';
      } else {
        const limit = Number(this.dataset.closestToShow) || 4;
        list = scored
          .filter((product) => product.score > 0)
          .sort((a, b) => b.score - a.score || a.order - b.order)
          .slice(0, limit);
        mode = list.length ? 'close' : 'none';
      }

      // Cards
      const differsLabel = this.grid.dataset.differsLabel || '';
      const align = this.grid.dataset.textAlign || 'center';
      const fragment = document.createDocumentFragment();
      list.forEach((product) => {
        const item = product.el.cloneNode(true);
        if (mode === 'close' && product.missed.length) {
          const note = document.createElement('p');
          note.className = 'racket-quiz__differs';
          note.textContent = `${differsLabel} ${product.missed.map((answer) => answer.name).join(', ')}`.trim();

          // The wrapper takes the same alignment as the card text (left / center / right)
          const wrapper = document.createElement('div');
          wrapper.className = `racket-quiz__differs-wrapper text-${align}`;
          wrapper.appendChild(note);
          this.placeNote(item, wrapper);
        }
        fragment.appendChild(item);
      });
      this.grid.textContent = '';
      this.grid.appendChild(fragment);
      this.grid.hidden = list.length === 0;

      // Message
      this.querySelectorAll('[data-quiz-message]').forEach((message) => {
        message.hidden = message.dataset.quizMessage !== mode;
      });
      const count = this.querySelector('[data-quiz-count]');
      if (count) count.textContent = list.length;

      // Answer summary
      this.summary.textContent = '';
      answers.forEach((answer) => {
        const chip = document.createElement('li');
        chip.textContent = answer.label;
        this.summary.appendChild(chip);
      });

      this.questions.hidden = true;
      this.results.hidden = false;
      this.resultsTitle.focus({ preventScroll: true });
      this.bringIntoView(this.results);
    }

    /* ---------- Helpers ---------- */

    // Puts the "Differs on" note inside the product card, directly below the price.
    // Falls back to the end of the card's text area if the price can't be found.
    placeNote(item, note) {
      const price = item.querySelector('.price');
      if (price) {
        price.insertAdjacentElement('afterend', note);
        return;
      }
      const info = item.querySelector('.card-information, .card__information');
      (info || item).appendChild(note);
    }

    bringIntoView(element) {
      const top = element.getBoundingClientRect().top;
      if (top >= 0 && top <= window.innerHeight * 0.5) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      element.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
  }

  customElements.define('racket-quiz', RacketQuiz);
}